import { Loader2 } from 'lucide-react';
import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerProfile } from '../../hooks/usePartnerSettings';
import type { Partner } from '../../lib/api';
import { SETTINGS_STRINGS } from '../../lib/constants';

interface ThemeStyles {
  background?: string;
  foreground?: string;
  card?: string;
  'card-foreground'?: string;
  popover?: string;
  'popover-foreground'?: string;
  primary?: string;
  'primary-foreground'?: string;
  secondary?: string;
  'secondary-foreground'?: string;
  muted?: string;
  'muted-foreground'?: string;
  accent?: string;
  'accent-foreground'?: string;
  destructive?: string;
  'destructive-foreground'?: string;
  border?: string;
  input?: string;
  ring?: string;
  'chart-1'?: string;
  'chart-2'?: string;
  'chart-3'?: string;
  'chart-4'?: string;
  'chart-5'?: string;
  radius?: string;
  'font-body'?: string;
  'font-heading'?: string;
  [key: string]: string | undefined;
}

interface ThemeConfig {
  styles?: ThemeStyles;
  darkMode?: boolean;
}

/**
 * Theme Configuration Tab Component
 * Allows partners to customize their SDK theme
 */
export const ThemeTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [formData, setFormData] = React.useState<Partial<Partner>>({});

  React.useEffect(() => {
    if (partner) {
      setFormData({
        themeConfig: partner.themeConfig,
      });
    }
  }, [partner]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({ themeConfig: formData.themeConfig });
  };

  const handleThemeStyleChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      themeConfig: {
        ...((prev.themeConfig as ThemeConfig) || {}),
        styles: {
          ...((prev.themeConfig as ThemeConfig)?.styles || {}),
          [key]: value,
        },
      },
    }));
  };

  // Convert HSL to hex for color picker
  const hslToHex = (hsl: string): string => {
    if (!hsl) return '#000000';
    const match = hsl.match(/(\d+\.?\d*)\s+(\d+\.?\d*)%\s+(\d+\.?\d*)%/);
    if (!match) return '#000000';

    const h = parseFloat(match[1]);
    const s = parseFloat(match[2]) / 100;
    const l = parseFloat(match[3]) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;
    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    const toHex = (n: number) => {
      const hex = Math.round((n + m) * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Convert hex to HSL
  const hexToHsl = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '';

    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const strings = SETTINGS_STRINGS.THEME_CONFIGURATION;

  const colorFields = [
    { key: 'background', label: 'Background', default: '0 0% 100%' },
    { key: 'foreground', label: 'Foreground', default: '225 15% 16%' },
    { key: 'card', label: 'Card', default: '0 0% 100%' },
    {
      key: 'card-foreground',
      label: 'Card Foreground',
      default: '225 15% 16%',
    },
    { key: 'popover', label: 'Popover', default: '0 0% 100%' },
    {
      key: 'popover-foreground',
      label: 'Popover Foreground',
      default: '225 15% 16%',
    },
    { key: 'primary', label: 'Primary', default: '210 30% 25%' },
    {
      key: 'primary-foreground',
      label: 'Primary Foreground',
      default: '0 0% 100%',
    },
    { key: 'secondary', label: 'Secondary', default: '210 15% 94%' },
    {
      key: 'secondary-foreground',
      label: 'Secondary Foreground',
      default: '210 30% 25%',
    },
    { key: 'muted', label: 'Muted', default: '210 10% 96%' },
    {
      key: 'muted-foreground',
      label: 'Muted Foreground',
      default: '220 10% 45%',
    },
    { key: 'accent', label: 'Accent', default: '210 15% 94%' },
    {
      key: 'accent-foreground',
      label: 'Accent Foreground',
      default: '210 30% 25%',
    },
    { key: 'destructive', label: 'Destructive', default: '0 84.2% 60.2%' },
    {
      key: 'destructive-foreground',
      label: 'Destructive Foreground',
      default: '0 0% 100%',
    },
    { key: 'border', label: 'Border', default: '220 13% 90%' },
    { key: 'input', label: 'Input', default: '220 13% 90%' },
    { key: 'ring', label: 'Ring', default: '210 30% 25%' },
  ];

  const chartFields = [
    { key: 'chart-1', label: 'Chart 1', default: '210 50% 50%' },
    { key: 'chart-2', label: 'Chart 2', default: '260 50% 55%' },
    { key: 'chart-3', label: 'Chart 3', default: '150 50% 45%' },
    { key: 'chart-4', label: 'Chart 4', default: '325 50% 55%' },
    { key: 'chart-5', label: 'Chart 5', default: '45 80% 55%' },
  ];

  return (
    <div className="space-y-6">
      {/* Theme Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Color Styles */}
              <div>
                <h4 className="text-sm font-medium mb-2">
                  {strings.COLOR_STYLES_LABEL}
                </h4>
                <div className="grid grid-cols-1 gap-2">
                  {colorFields.map(field => {
                    const hslValue =
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.[field.key] || field.default;
                    const hexValue = hslValue ? hslToHex(hslValue) : '#000000';

                    return (
                      <div key={field.key} className="flex items-center gap-2">
                        <Label
                          htmlFor={`theme-${field.key}`}
                          className="text-xs w-24 flex-shrink-0">
                          {field.label}
                        </Label>
                        <input
                          type="color"
                          value={hexValue}
                          onChange={e =>
                            handleThemeStyleChange(
                              field.key,
                              hexToHsl(e.target.value),
                            )
                          }
                          className="w-8 h-8 rounded border border-input cursor-pointer"
                          title="Pick a color"
                        />
                        <Input
                          id={`theme-${field.key}`}
                          value={hslValue}
                          onChange={e =>
                            handleThemeStyleChange(field.key, e.target.value)
                          }
                          placeholder={`e.g., ${field.default}`}
                          className="text-xs font-mono flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Chart Colors */}
              <div>
                <h4 className="text-sm font-medium mb-2">Chart Colors</h4>
                <div className="grid grid-cols-1 gap-2">
                  {chartFields.map(field => {
                    const hslValue =
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.[field.key] || field.default;
                    const hexValue = hslValue ? hslToHex(hslValue) : '#000000';

                    return (
                      <div key={field.key} className="flex items-center gap-2">
                        <Label
                          htmlFor={`theme-${field.key}`}
                          className="text-xs w-24 flex-shrink-0">
                          {field.label}
                        </Label>
                        <input
                          type="color"
                          value={hexValue}
                          onChange={e =>
                            handleThemeStyleChange(
                              field.key,
                              hexToHsl(e.target.value),
                            )
                          }
                          className="w-8 h-8 rounded border border-input cursor-pointer"
                          title="Pick a color"
                        />
                        <Input
                          id={`theme-${field.key}`}
                          value={hslValue}
                          onChange={e =>
                            handleThemeStyleChange(field.key, e.target.value)
                          }
                          placeholder={`e.g., ${field.default}`}
                          className="text-xs font-mono flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Typography */}
              <div>
                <h4 className="text-sm font-medium mb-2">Typography</h4>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="font-body" className="text-xs">
                      Body Font
                    </Label>
                    <Input
                      id="font-body"
                      value={
                        (
                          (formData.themeConfig as ThemeConfig)
                            ?.styles as ThemeStyles
                        )?.['font-body'] || ''
                      }
                      onChange={e => {
                        const customValue = e.target.value;
                        handleThemeStyleChange('font-body', customValue);
                      }}
                      placeholder="Inter, system-ui, sans-serif"
                      className="text-sm font-mono"
                      style={{
                        fontFamily:
                          (
                            (formData.themeConfig as ThemeConfig)
                              ?.styles as ThemeStyles
                          )?.['font-body'] || 'Inter, system-ui, sans-serif',
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="font-heading" className="text-xs">
                      Heading Font
                    </Label>
                    <Input
                      id="font-heading"
                      value={
                        (
                          (formData.themeConfig as ThemeConfig)
                            ?.styles as ThemeStyles
                        )?.['font-heading'] || ''
                      }
                      onChange={e => {
                        const customValue = e.target.value;
                        handleThemeStyleChange('font-heading', customValue);
                      }}
                      placeholder="Plus Jakarta Sans, system-ui, sans-serif"
                      className="text-sm font-mono"
                      style={{
                        fontFamily:
                          (
                            (formData.themeConfig as ThemeConfig)
                              ?.styles as ThemeStyles
                          )?.['font-heading'] ||
                          'Plus Jakarta Sans, system-ui, sans-serif',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Border Radius */}
              <div>
                <h4 className="text-sm font-medium mb-2">
                  {strings.BORDER_RADIUS_LABEL}
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-xs">Radius</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="2"
                        step="0.125"
                        value={parseFloat(
                          (
                            (formData.themeConfig as ThemeConfig)
                              ?.styles as ThemeStyles
                          )?.radius || '0.625',
                        )}
                        onChange={e => {
                          const val =
                            e.target.value === '' ? 0 : Number(e.target.value);
                          handleThemeStyleChange(
                            'radius',
                            `${Math.min(2, Math.max(0, val))}rem`,
                          );
                        }}
                        className="w-16 text-xs text-right"
                      />
                      <span className="text-xs font-medium">rem</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.125"
                    value={parseFloat(
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.radius || '0.625',
                    )}
                    onChange={e =>
                      handleThemeStyleChange('radius', `${e.target.value}rem`)
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                    style={{
                      background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${(parseFloat(((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.radius || '0.625') / 2) * 100}%, #e5e7eb ${(parseFloat(((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.radius || '0.625') / 2) * 100}%, #e5e7eb 100%)`,
                    }}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0rem (sharp)</span>
                    <span>2rem (very round)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (partner) {
                      setFormData({
                        themeConfig: partner.themeConfig,
                      });
                    }
                  }}
                  disabled={isUpdating}>
                  {SETTINGS_STRINGS.ORGANIZATION_PROFILE.CANCEL_BUTTON}
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    SETTINGS_STRINGS.ORGANIZATION_PROFILE.SAVE_BUTTON
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Live Preview Panel */}
        <Card className="sticky top-6 self-start">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
            <div
              className="border p-6 space-y-4"
              style={{
                backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.background || '0 0% 100%'})`,
                color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.foreground || '225 15% 16%'})`,
                borderColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.border || '220 13% 90%'})`,
                borderRadius:
                  ((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)
                    ?.radius || '0.625rem',
                fontFamily:
                  (
                    (formData.themeConfig as ThemeConfig)?.styles as ThemeStyles
                  )?.['font-body'] || 'Inter, system-ui, sans-serif',
              }}>
              {/* Heading Example */}
              <h3
                className="text-xl font-bold"
                style={{
                  fontFamily:
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.['font-heading'] ||
                    'Plus Jakarta Sans, system-ui, sans-serif',
                }}>
                Theme Preview
              </h3>

              {/* Card Example */}
              <div
                className="border p-4 space-y-2"
                style={{
                  backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.card || '0 0% 100%'})`,
                  color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['card-foreground'] || '225 15% 16%'})`,
                  borderColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.border || '220 13% 90%'})`,
                  borderRadius:
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.radius || '0.625rem',
                }}>
                <h5
                  className="font-semibold"
                  style={{
                    fontFamily:
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.['font-heading'] ||
                      'Plus Jakarta Sans, system-ui, sans-serif',
                  }}>
                  Card Title
                </h5>
                <p className="text-sm">This is an example card component</p>
              </div>

              {/* Popover Example */}
              <div
                className="inline-block border p-3 shadow-lg"
                style={{
                  backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.popover || '0 0% 100%'})`,
                  color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['popover-foreground'] || '225 15% 16%'})`,
                  borderColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.border || '220 13% 90%'})`,
                  borderRadius:
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.radius || '0.625rem',
                }}>
                <p className="text-sm">Popover content</p>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.primary || '210 30% 25%'})`,
                    color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['primary-foreground'] || '0 0% 100%'})`,
                    borderRadius:
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.radius || '0.625rem',
                  }}>
                  Primary Button
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.secondary || '210 15% 94%'})`,
                    color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['secondary-foreground'] || '210 30% 25%'})`,
                    borderRadius:
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.radius || '0.625rem',
                  }}>
                  Secondary
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.destructive || '0 84.2% 60.2%'})`,
                    color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['destructive-foreground'] || '0 0% 100%'})`,
                    borderRadius:
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.radius || '0.625rem',
                  }}>
                  Destructive
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.accent || '210 15% 94%'})`,
                    color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['accent-foreground'] || '210 30% 25%'})`,
                    borderRadius:
                      (
                        (formData.themeConfig as ThemeConfig)
                          ?.styles as ThemeStyles
                      )?.radius || '0.625rem',
                  }}>
                  Accent
                </button>
              </div>

              {/* Muted Section */}
              <div
                className="p-3"
                style={{
                  backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.muted || '210 10% 96%'})`,
                  color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.['muted-foreground'] || '220 10% 45%'})`,
                  borderRadius:
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.radius || '0.625rem',
                }}>
                <p className="text-sm">Muted section with muted text</p>
              </div>

              {/* Input Example */}
              <input
                type="text"
                placeholder="Input field"
                className="w-full px-3 py-2 text-sm focus:outline-none"
                style={{
                  backgroundColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.background || '0 0% 100%'})`,
                  color: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.foreground || '225 15% 16%'})`,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: `hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.input || '220 13% 90%'})`,
                  borderRadius:
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.radius || '0.625rem',
                  outline: `2px solid transparent`,
                }}
                onFocus={e => {
                  e.target.style.outline = `2px solid hsl(${((formData.themeConfig as ThemeConfig)?.styles as ThemeStyles)?.ring || '210 30% 25%'})`;
                  e.target.style.outlineOffset = '2px';
                }}
                onBlur={e => {
                  e.target.style.outline = '2px solid transparent';
                }}
              />

              {/* Chart Colors Preview */}
              <div className="flex gap-2">
                {chartFields.map(field => {
                  const hslValue =
                    (
                      (formData.themeConfig as ThemeConfig)
                        ?.styles as ThemeStyles
                    )?.[field.key] || field.default;
                  return (
                    <div
                      key={field.key}
                      className="flex-1 h-12 rounded"
                      style={{
                        backgroundColor: `hsl(${hslValue})`,
                        borderRadius:
                          (
                            (formData.themeConfig as ThemeConfig)
                              ?.styles as ThemeStyles
                          )?.radius || '0.625rem',
                      }}
                      title={field.label}
                    />
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
